
import { Vue, Component, Prop, Watch} from "vue-property-decorator";

import GET_COMPANY_PROFILE from "@/apollo/profile/GetCompanyProfile.gql";
import CREATE_COMPANY from "@/apollo/company/CreateCompany.gql";
import UPDATE_COMPANY from "@/apollo/company/UpdateCompany.gql";

import LoadingOverlay from "@/components/common/LoadingOverlay.vue";
import FormPhoto from "@/components/form/FormPhoto.vue";
import FormInput from "@/components/form/FormInput.vue";
import FormTextarea from "@/components/form/FormTextarea.vue";
import FormGroup from "@/components/form/FormGroup.vue";
import UiButton from "@/components/button/ui-button.vue";
import StateProvinceSelect from "@/components/autocompleteMultiselects/StateProvinceSelect.vue";
import ProfileCountrySelect from "@/components/autocompleteMultiselects/ProfileCountrySelect.vue";

import { Company, User } from "@/generated/graphql";
import { NOTIFICATION } from "@/entities/Events";
import Message from "@/data/notificationsMessages";
import DropdownOption from "@/entities/DropdownOption";
import RegionInput from "@/components/autocompleteMultiselects/RegionInput.vue";
import getStateValidationRules from "@/helpers/locations/stateValidationRules";


@Component({
  components: {
    RegionInput,
    FormPhoto,
    FormTextarea,
    FormGroup,
    UiButton,
    LoadingOverlay,
    FormInput,
    StateProvinceSelect,
    ProfileCountrySelect,
  },
  apollo: {
    companyFromDb: {
      query: GET_COMPANY_PROFILE,
      variables() {
        return {
          id: this.company.id,
        };
      },
      update (data) {
        return data.company_by_pk;
      },
      skip() {
        return this.skipQuery;
      },
    },
  },
})
export default class CompanyProfile extends Vue {
  @Prop({type: Object, required: true}) readonly company!: Company;
  @Prop({type: Object, required: true}) readonly currentUser!: User;

  companyFromDb: Company | null = null;
  companyObj: Partial<Company> | null = null;
  processing: boolean = false;
  country: DropdownOption[] = [];
  state: DropdownOption[] = [];

  mounted() {
    if (this.company.id) {
      this.$apollo.queries.companyFromDb.skip = false;
    }
  }

  @Watch('companyFromDb', {immediate: true})
  onCompanyFromDbChange(newVal: Company | null, oldVal: Company | null) {
    if (!oldVal && newVal != oldVal) {
      if (!this.companyFromDb) {
        return;
      }

      const { __typename, ...company } = this.companyFromDb;

      this.companyObj = {
        ...company,
      };

      const country = this.companyObj?.country;
      const state = this.companyObj?.state;
      this.country = country ? [{ id: country, name: country }] : [];
      this.state = state ? [{ id: state, name: state }] : [];
    }
  }

  @Watch("countryName")
  clearCountry(newCountry: string): void {
    if (newCountry !== this.companyFromDb?.country || !Boolean(newCountry)) {
      this.state = [];
      this.companyObj!.city = "";
    }
  }

  @Watch("state")
  clearCity(newState: DropdownOption[]): void {
    const stateName = newState[0]?.name;
    if (stateName !== this.companyFromDb?.state) {
      this.companyObj!.city = "";
    }
  }

  @Watch("companyObj.company_logo")
  async watchCompanyLogo(avatar: string): Promise<void> {
    if (avatar === this.companyFromDb?.company_logo) {
      return;
    }

    await this.updateCompanyLogo();
  }

  get stateValidationRules(): string | undefined {
    return getStateValidationRules(this.countryName);
  }

  get countryName(): string {
    return this.country[0]?.name || "";
  }

  async onSubmit(): Promise<void> {
    this.processing = true;

    try {
      if (this.company.id) {
        await this.updateCompanyInfo();
        this.$eventBus.emit(
          NOTIFICATION,
          Message.successMessage("Updated successfully")
        );
      } else {
        await this._createCompany();
      }
    } catch (err) {
      this.$eventBus.emit(
        NOTIFICATION,
        Message.errorMessage(err.message)
      );
    } finally {
      this.processing = false;
    }
  }

  async updateCompanyInfo(): Promise<void> {
    const phone_number = parseInt(String(this.companyObj?.phone_number).replace(/\s/g, ""))
    const number_of_employee = parseInt(String(this.companyObj?.number_of_employee));
    const country = this.country[0].name;
    const state = this.state[0]?.name;
    await this._updateCompany({
      ...this.companyObj,
      country,
      state,
      phone_number,
      number_of_employee,
    });
  }

  async updateCompanyLogo(): Promise<void> {
    await this._updateCompany({
      company_logo: this.companyObj?.company_logo,
    });
  }

  async _createCompany(): Promise<any> {
    const country = this.country[0].name;
    const state = this.state[0].name;
    const phone_number = parseInt(String(this.companyObj?.phone_number).replace(/\s/g, ""))
    const number_of_employee = parseInt(String(this.companyObj?.number_of_employee));
    const variables = {
      object: {
        ...this.companyObj,
        country,
        state,
        phone_number,
        number_of_employee,
        admins: {
          data: {
            user_id: this.currentUser.id,
          },
        },
      },
    };

    return await this.$apollo.mutate({
      mutation: CREATE_COMPANY,
      variables,
    });
  }

  async _updateCompany(_set: Partial<Company>): Promise<any> {
    return await this.$apollo.mutate({
      mutation: UPDATE_COMPANY,
      variables: {
        id: this.company.id,
        _set,
      },
    });
  }
}
